import { Tooltip } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import { Download, ScanEyeIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "src/components/ui/button";
import { useLanguage } from "../../../../../context/LanguageContext";
import DialogDeleteHomeWork from "./DialogDeleteHomeWork";
import DrawerAddHomeWork from "./DrawerAddHomeWork";
import DrawerEditHomeWork from "./DrawerEditHomeWork";
import { SubLessonData } from "../../../../../lib/languages";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../../../../lib/api";
import { useAuth } from "../../../../../context/AuthContext";
import DialogAnswerHomeWork from "./Answer/DialogAnswerHomeWork";
import axios from "axios";
import { toast } from "sonner";
import { DialogShowAnswer } from "./Answer/DialogShowAnswer";

export default function HomeWorkTab({ assignments, fetchAssignments }) {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const { id: lessonId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  const [hoveredCard, setHoveredCard] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);

  // Fetch answers for a given assignment
  const fetchAnswers = async (assignmentId) => {
    setLoadingAnswers(true);
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/lesson/${lessonId}/all-answer/${assignmentId}`
      );
      setAnswers(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingAnswers(false);
    }
  };

  return (
    <div className="grid gap-4">
      {assignments.length !== 0 ? (
        assignments.map((docs) => (
          <div
            key={docs.id}
            onMouseEnter={() => setHoveredCard(docs.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="flex items-center gap-2 relative py-2 flex-wrap">
              <div className="flex-1 flex items-center gap-2">
                <span className="font-semibold text-xl">
                  {docs?.title[language] || docs?.title.en || docs?.title.ar}
                </span>
                {" / "}
                <div className="flex items-center gap-2">
                  <Tooltip title={t.homeworkExpireDateTooltip}>
                    <p className="leading-relaxed text-xs text-muted-foreground">
                      {docs.due_date}
                    </p>
                  </Tooltip>
                  {" / "}
                  <Tooltip title={t.homeworkMaxFilesTooltip}>
                    <p className="leading-relaxed text-xs text-muted-foreground">
                      {docs.max_items}{" "}
                      {docs.max_items === 1
                        ? t.fileLabelSingular
                        : t.fileLabelPlural}
                    </p>
                  </Tooltip>
                </div>
              </div>

              {isTeacher && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary"
                  onClick={() =>
                    navigate(`/teacher/show-homework-answers/${docs.id}`, {
                      state: { lessonId },
                    })
                  }
                >
                  {t.homeworkShowAnswers}
                  <ScanEyeIcon className="h-4 w-4 ml-1 text-blue-600" />
                </Button>
              )}

              <span className="font-semibold text-xl">{docs.score} Score</span>

              {/* Action buttons on hover */}
              <AnimatePresence>
                {hoveredCard === docs.id && isTeacher ? (
                  <>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <DrawerEditHomeWork
                        fetchAssignments={fetchAssignments}
                        item={docs}
                      />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, delay: 0.05 }}
                    >
                      <DialogDeleteHomeWork
                        item={docs}
                        fetchAssignments={fetchAssignments}
                      />
                    </motion.div>
                  </>
                ) : (
                  hoveredCard === docs.id &&
                  !isTeacher && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <DialogAnswerHomeWork
                          item={docs}
                          fetchAnswers={() => fetchAnswers(docs.id)}
                        />
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2, delay: 0.05 }}
                      >
                        <DialogShowAnswer
                          answers={answers}
                          loading={loadingAnswers}
                          onOpen={() => fetchAnswers(docs.id)}
                        />
                      </motion.div>
                    </>
                  )
                )}
              </AnimatePresence>
            </div>

            {docs?.files &&
              docs.files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-4 border-b px-5"
                >
                  <div className="flex items-center gap-3">
                    {FileSvg}
                    <span className="font-medium">
                      {docs.title[language] || docs.title.en || docs.title.ar}{" "}
                      {index + 1}
                    </span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary"
                    asChild
                  >
                    <a href={`${UrlImage}${file}`} download target="_blank">
                      <Download className="h-4 w-4 ml-1 text-blue-600" />{" "}
                      {t.download || "Download"}
                    </a>
                  </Button>
                </div>
              ))}
          </div>
        ))
      ) : (
        <div className="p-4 text-center text-gray-500">{t.noDocuments}</div>
      )}

      {isTeacher && <DrawerAddHomeWork fetchAssignments={fetchAssignments} />}
    </div>
  );
}

const FileSvg = (
  <svg
    width="24"
    height="31"
    viewBox="0 0 24 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.07157 0.5C1.95345 0.5 0.214844 2.2386 0.214844 4.35672V26.6433C0.214844 28.7614 1.95345 30.5 4.07157 30.5H19.9296C22.0477 30.5 23.7863 28.7614 23.7863 26.6433V9.07143H18.4291C16.667 9.07143 15.2148 7.61932 15.2148 5.85714V0.5H4.07157ZM17.3577 0.661133V5.85714C17.3577 6.46924 17.817 6.92857 18.4291 6.92857H23.6251C23.4514 6.36196 23.1414 5.84003 22.7148 5.4135L18.8728 1.57143C18.4462 1.14491 17.9243 0.834887 17.3577 0.661133ZM6.64342 12.2857H17.3577C17.6419 12.2857 17.9144 12.3986 18.1153 12.5995C18.3162 12.8005 18.4291 13.073 18.4291 13.3571C18.4291 13.6413 18.3162 13.9138 18.1153 14.1148C17.9144 14.3157 17.6419 14.4286 17.3577 14.4286H6.64342C6.35926 14.4286 6.08673 14.3157 5.8858 14.1148C5.68487 13.9138 5.57199 13.6413 5.57199 13.3571C5.57199 13.073 5.68487 12.8005 5.8858 12.5995C6.08673 12.3986 6.35926 12.2857 6.64342 12.2857ZM6.64342 17.6429H17.3577C17.6419 17.6429 17.9144 17.7557 18.1153 17.9567C18.3162 18.1576 18.4291 18.4301 18.4291 18.7143C18.4291 18.9984 18.3162 19.271 18.1153 19.4719C17.9144 19.6728 17.6419 19.7857 17.3577 19.7857H6.64342C6.35926 19.7857 6.08673 19.6728 5.8858 19.4719C5.68487 19.271 5.57199 18.9984 5.57199 18.7143C5.57199 18.4301 5.68487 18.1576 5.8858 17.9567C6.08673 17.7557 6.35926 17.6429 6.64342 17.6429ZM6.64342 23H17.3577C17.6419 23 17.9144 23.1129 18.1153 23.3138C18.3162 23.5147 18.4291 23.7873 18.4291 24.0714C18.4291 24.3556 18.3162 24.6281 18.1153 24.829C17.9144 25.03 17.6419 25.1429 17.3577 25.1429H6.64342C6.35926 25.1429 6.08673 25.03 5.8858 24.829C5.68487 24.6281 5.57199 24.3556 5.57199 24.0714C5.57199 23.7873 5.68487 23.5147 5.8858 23.3138C6.08673 23.1129 6.35926 23 6.64342 23Z"
      fill="url(#paint0_linear_444_1681)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_444_1681"
        x1="-2.94587"
        y1="0.553572"
        x2="26.947"
        y2="30.4464"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="#00D8FF" />
        <stop offset="1" stop-color="#0000FF" />
      </linearGradient>
    </defs>
  </svg>
);
